/**
 * Energy Certificate Simulator - TypeScript Type Definitions
 *
 * Based on Polish building law:
 * - Dz.U. 2023 poz. 697 (EP calculation methodology)
 * - PN-EN ISO 52016-1:2017-09 (calculation standards)
 * - WT 2021 (building thermal requirements)
 *
 * @license Proprietary - Commercial module
 */

// =============================================================================
// BUILDING PERIOD TYPES
// =============================================================================

/**
 * Polish building construction periods
 * Each period has different default U-values based on thermal regulations
 */
export type BuildingPeriod =
  | 'before_1945'  // Pre-war, no insulation, solid brick
  | '1945_1966'    // Post-war, large panel (wielka płyta), poor insulation
  | '1967_1985'    // Improved large panel
  | '1986_1998'    // First thermal requirements
  | '1999_2008'    // WT 1998 regulations
  | '2009_2013'    // WT 2008 regulations
  | '2014_2020'    // WT 2013 regulations
  | '2021_plus';   // WT 2021 regulations (current)

/**
 * Building type classification
 * Affects shape coefficient (współczynnik kształtu) calculation
 */
export type BuildingType =
  | 'detached'      // Wolnostojący - free-standing house
  | 'semi_detached' // Bliźniak - semi-detached/twin house
  | 'terraced'      // Szeregowy - terraced/row house
  | 'apartment';    // Apartament - apartment/flat

/**
 * Apartment floor position
 * Affects heat loss through ceiling/floor
 */
export type ApartmentFloorPosition =
  | 'ground'   // Parter - ground floor (heat loss through floor)
  | 'middle'   // Środkowe - middle floor (minimal vertical heat loss)
  | 'top';     // Ostatnie piętro - top floor (heat loss through roof)

/**
 * Apartment orientation
 * Affects solar heat gains
 */
export type ApartmentOrientation =
  | 'north'      // Północ - minimal solar gains
  | 'south'      // Południe - maximum solar gains
  | 'east'       // Wschód - morning solar gains
  | 'west'       // Zachód - afternoon solar gains
  | 'northeast'  // Północny-wschód
  | 'northwest'  // Północny-zachód
  | 'southeast'  // Południowy-wschód
  | 'southwest'; // Południowy-zachód

// =============================================================================
// RENOVATION TYPES
// =============================================================================

/**
 * Thermal renovation status
 * Whether and to what extent the building has been thermally renovated
 */
export type RenovationStatus =
  | 'none'     // Nie - no renovation
  | 'partial'  // Tak, częściowo - partial renovation (e.g., only walls or only roof)
  | 'full';    // Tak - full thermal renovation (walls + roof + windows)

/**
 * Polish climate zone (per PN-EN 12831)
 * Zone III is the national average used in standard calculations
 */
export type ClimateZone =
  | 'I'    // Warmest: Szczecin, Wrocław, Zielona Góra (HDD ~3200)
  | 'II'   // Moderate-warm: Poznań, Łódź, Kraków (HDD ~3400)
  | 'III'  // Average: Warszawa, Lublin, Rzeszów (HDD ~3600)
  | 'IV'   // Moderate-cold: Olsztyn, Białystok, Kielce (HDD ~3900)
  | 'V';   // Coldest: Suwałki, Zakopane, mountain regions (HDD ~4200)

// =============================================================================
// INSULATION TYPES
// =============================================================================

/**
 * Insulation quality level
 * Determines U-value for walls and roof
 */
export type InsulationLevel =
  | 'none'     // Brak izolacji - no insulation
  | 'partial'  // Częściowa - partial insulation
  | 'full'     // Pełna - full insulation
  | 'modern';  // Nowoczesna - modern high-performance

/**
 * Window glazing type
 * Each type has specific U-value [W/m²K]
 */
export type WindowType =
  | 'single'      // Pojedyncze - single glazing (U=5.0)
  | 'double_old'  // Podwójne stare - old double glazing (U=2.8)
  | 'double_new'  // Podwójne nowe - new double glazing (U=1.3)
  | 'triple';     // Trzyszybowe - triple glazing (U=0.8)

// =============================================================================
// HEATING SYSTEM TYPES
// =============================================================================

/**
 * Primary heating system classification
 * Determines efficiency (η) and primary energy coefficient (wi)
 */
export type HeatingSystem =
  | 'gas_condensing'      // Kocioł gazowy kondensacyjny (wi=1.1, η=0.97)
  | 'gas_traditional'     // Kocioł gazowy tradycyjny (wi=1.1, η=0.90)
  | 'coal'                // Kocioł węglowy (wi=1.1, η=0.80)
  | 'biomass'             // Kocioł na biomasę (wi=0.2, η=0.85)
  | 'heat_pump_air'       // Pompa ciepła powietrze-woda (wi=2.5/COP)
  | 'heat_pump_ground'    // Pompa ciepła gruntowa (wi=2.5/COP)
  | 'electric_radiators'  // Ogrzewanie elektryczne - grzejniki (wi=2.5, η=1.0)
  | 'electric_floor'      // Ogrzewanie elektryczne - podłogowe (wi=2.5, η=1.0)
  | 'district_heating'    // Ciepło sieciowe (wi=0.8-1.3, η=0.95)
  | 'stove';              // Piec kaflowy/kominek (wi=1.1, η=0.70)

/**
 * Heating system age category
 * Affects actual efficiency
 */
export type SystemAge =
  | 'new'    // <5 lat - new system, optimal efficiency
  | 'medium' // 5-15 lat - medium age, reduced efficiency
  | 'old';   // >15 lat - old system, lowest efficiency

/**
 * Domestic hot water (DHW) source
 * Can be combined with heating or separate
 */
export type HotWaterSource =
  | 'combined'   // Wspólne z c.o. - combined with central heating
  | 'electric'   // Elektryczne - electric water heater
  | 'gas'        // Gazowe - gas water heater
  | 'solar';     // Solary - solar thermal collectors

// =============================================================================
// RENEWABLE ENERGY TYPES
// =============================================================================

/**
 * Renewable energy sources (excluding PV which is tracked separately)
 */
export type RenewableSource =
  | 'solar_thermal'    // Kolektory słoneczne - solar thermal for DHW
  | 'recuperation'     // Rekuperacja - heat recovery ventilation
  | 'ground_exchanger'; // Gruntowy wymiennik ciepła

// =============================================================================
// ENERGY CLASS TYPES
// =============================================================================

/**
 * Energy performance class (A+ to G)
 * Based on EP indicator [kWh/m²·year]
 *
 * Thresholds for residential buildings:
 * - A+: EP < 0 (plus-energy building)
 * - A:  0 ≤ EP < 63
 * - B:  63 ≤ EP < 157
 * - C:  157 ≤ EP < 250
 * - D:  250 ≤ EP < 344
 * - E:  344 ≤ EP < 438
 * - F:  438 ≤ EP < 531
 * - G:  EP ≥ 531
 */
export type EnergyClass = 'A+' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

// =============================================================================
// INPUT/OUTPUT INTERFACES
// =============================================================================

/**
 * Simulator input data from the form
 * Contains all 12 parameters required for EP calculation
 */
export interface SimulatorInput {
  // === Building parameters (4 + 2 optional for apartments) ===
  /** Usable floor area [m²], range: 20-500 */
  usableArea: number;
  /** Building construction period */
  buildingPeriod: BuildingPeriod;
  /** Building type/form */
  buildingType: BuildingType;
  /** Number of floors, range: 1-4+ */
  floors: number;
  /** Number of occupants, range: 1-5+ */
  occupants: number;
  /** Floor position (only for apartments) */
  apartmentFloorPosition?: ApartmentFloorPosition;
  /** Apartment orientation (only for apartments) */
  apartmentOrientation?: ApartmentOrientation;
  /** Climate zone (default: III - national average) */
  climateZone?: ClimateZone;

  // === Renovation parameters (2) ===
  /** Thermal renovation status */
  renovationStatus: RenovationStatus;
  /** Year of thermal renovation (only if renovated) */
  renovationYear?: number;

  // === Insulation parameters (3) ===
  /** External wall insulation level */
  wallInsulation: InsulationLevel;
  /** Roof/attic insulation level */
  roofInsulation: InsulationLevel;
  /** Window glazing type */
  windowType: WindowType;

  // === Heating parameters (3 + 3 optional) ===
  /** Primary heating system */
  heatingSystem: HeatingSystem;
  /** Heating system age */
  systemAge: SystemAge;
  /** Domestic hot water source */
  hotWaterSource: HotWaterSource;
  /** Has secondary heating system */
  hasSecondaryHeating: boolean;
  /** Secondary heating system type (only if hasSecondaryHeating) */
  secondaryHeatingSystem?: HeatingSystem;
  /** Secondary system share of total heating [%], range: 10-50 */
  secondaryHeatingShare?: number;

  // === Renewable energy parameters (2) ===
  /** Has photovoltaic installation */
  hasPV: boolean;
  /** PV system power [kWp], only if hasPV is true */
  pvPower?: number;
  /** Other renewable energy sources */
  renewables: RenewableSource[];

  // === Hot water overrides (advanced, all optional) ===
  /** Daily hot water consumption per person [l/day], default: 40 */
  dailyHotWaterPerPerson?: number;
  /** Hot water target temperature [°C], default: 55 */
  hotWaterTemp?: number;
  /** Cold water inlet temperature [°C], default: 10 */
  coldWaterTemp?: number;
}

/**
 * Modernization recommendation
 */
export interface Recommendation {
  /** Unique identifier */
  id: string;
  /** Short title (Polish) */
  title: string;
  /** Detailed description (Polish) */
  description: string;
  /** Estimated cost range [PLN] */
  estimatedCost: {
    min: number;
    max: number;
  };
  /** Estimated annual energy savings [kWh/year] */
  estimatedSavings: number;
  /** EP reduction potential [kWh/m²·year] */
  epReduction: number;
  /** Payback period range [years] */
  paybackYears: {
    min: number;
    max: number;
  };
  /** Priority level */
  priority: 'high' | 'medium' | 'low';
}

/**
 * Simulator calculation results
 */
export interface SimulatorResult {
  // === Primary indicators ===
  /** Total EP indicator [kWh/m²·year] */
  epTotal: number;
  /** EP for space heating [kWh/m²·year] */
  epHeating: number;
  /** EP for domestic hot water [kWh/m²·year] */
  epHotWater: number;
  /** EP for cooling [kWh/m²·year] (if applicable) */
  epCooling: number;

  // === Energy class ===
  /** Determined energy class (A+ to G) */
  energyClass: EnergyClass;

  // === Comparison with limits ===
  /** EP limit for new buildings (WT 2021) [kWh/m²·year] */
  limitEP: number;
  /** Whether EP exceeds the limit */
  exceedsLimit: boolean;

  // === Intermediate energy values (for visualization) ===
  /** Useful energy demand for space heating [kWh/year] */
  usefulEnergy: number;
  /** Final energy demand [kWh/year] */
  finalEnergy: number;
  /** Primary energy demand [kWh/year] */
  primaryEnergy: number;
  /** Useful energy demand for hot water [kWh/year] — for heating cost comparison */
  hotWaterUsefulEnergy: number;

  // === Recommendations ===
  /** List of modernization recommendations */
  recommendations: Recommendation[];
}

// =============================================================================
// INTERNAL CALCULATION TYPES
// =============================================================================

/**
 * Building geometry calculated from input parameters
 */
export interface BuildingGeometry {
  /** Usable floor area [m²] */
  floorArea: number;
  /** Heated volume [m³] */
  volume: number;
  /** External wall area [m²] */
  wallArea: number;
  /** Roof area [m²] */
  roofArea: number;
  /** Floor area (ground contact) [m²] */
  groundFloorArea: number;
  /** Window area [m²] */
  windowArea: number;
  /** Shape coefficient A/V [1/m] */
  shapeCoefficient: number;
}

/**
 * U-values for building envelope components [W/m²K]
 */
export interface UValues {
  /** External walls */
  walls: number;
  /** Roof/attic */
  roof: number;
  /** Windows */
  windows: number;
  /** Floor on ground */
  floor: number;
  /** Thermal bridges linear coefficient [W/mK] */
  thermalBridges: number;
}

/**
 * System efficiency parameters
 */
export interface SystemEfficiency {
  /** Heating system efficiency η [-] */
  heating: number;
  /** Distribution efficiency [-] */
  distribution: number;
  /** Regulation efficiency [-] */
  regulation: number;
  /** Total system efficiency [-] */
  total: number;
}

/**
 * Climate data for Poland
 */
export interface ClimateData {
  /** Average outdoor temperature during heating season [°C] */
  avgOutdoorTemp: number;
  /** Heating season length [hours] */
  heatingSeasonHours: number;
  /** Heating degree days [K·d/year] */
  heatingDegreeDays: number;
  /** Indoor design temperature [°C] */
  indoorTemp: number;
}

// =============================================================================
// FORM STATE TYPES
// =============================================================================

/**
 * Form validation errors
 */
export type SimulatorFormErrors = {
  [key: string]: string | undefined;
  usableArea?: string;
  buildingPeriod?: string;
  buildingType?: string;
  floors?: string;
  occupants?: string;
  renovationStatus?: string;
  renovationYear?: string;
  wallInsulation?: string;
  roofInsulation?: string;
  windowType?: string;
  heatingSystem?: string;
  systemAge?: string;
  hotWaterSource?: string;
  secondaryHeatingSystem?: string;
  secondaryHeatingShare?: string;
  pvPower?: string;
  renewables?: string;
};

/**
 * Wizard step identifier
 */
export type WizardStep = 'building' | 'insulation' | 'heating' | 'renewables' | 'results';

/**
 * Wizard navigation state
 */
export interface WizardState {
  /** Current active step */
  currentStep: WizardStep;
  /** Step completion status */
  completedSteps: Set<WizardStep>;
  /** Whether user can proceed to next step */
  canProceed: boolean;
}

// =============================================================================
// LOCAL STORAGE TYPES
// =============================================================================

/**
 * Saved simulation data for localStorage persistence
 */
export interface SavedSimulation {
  /** Unique identifier */
  id: string;
  /** Creation timestamp */
  createdAt: string;
  /** Last modified timestamp */
  updatedAt: string;
  /** User-provided name (optional) */
  name?: string;
  /** Input parameters */
  input: SimulatorInput;
  /** Calculation results (if completed) */
  result?: SimulatorResult;
}

/**
 * localStorage key constants
 */
export const STORAGE_KEYS = {
  /** Current form state */
  FORM_STATE: 'energy-cert-simulator-form',
  /** Saved simulations list */
  SAVED_SIMULATIONS: 'energy-cert-simulator-saved',
  /** User preferences */
  PREFERENCES: 'energy-cert-simulator-prefs',
} as const;

// =============================================================================
// IMPORT / EXPORT TYPES
// =============================================================================

import type { FuelPrice, InvestmentCostDefinition } from '../heating-cost/types/heatingCost.types';

/**
 * Heating cost settings embedded in export data.
 * Mirrors HeatingCostSettings from useHeatingCostConfig.
 */
export interface ExportHeatingCostSettings {
  discountRate: number;
  fuelPriceGrowthRate: number;
  analysisYears: number;
}

/**
 * Full simulator export data format for JSON import/export.
 * Contains all localStorage-persisted state for the simulator.
 */
export interface SimulatorExportData {
  /** Format version for backward compatibility */
  version: string;
  /** ISO timestamp of export */
  exportedAt: string;
  /** Current form state */
  formState: SimulatorInput;
  /** Saved simulations */
  savedSimulations: SavedSimulation[];
  /** User preferences */
  preferences: {
    showAdvancedOptions: boolean;
    autoSave: boolean;
    language: string;
  };
  /** Heating cost configuration */
  heatingCost: {
    fuelPrices: FuelPrice[];
    investmentCosts: InvestmentCostDefinition[];
    settings: ExportHeatingCostSettings;
  };
}

/**
 * Result of a JSON import operation.
 */
export interface ImportResult {
  /** Whether the import succeeded (data was written to localStorage) */
  success: boolean;
  /** Parsed and validated data (present if success=true) */
  data?: SimulatorExportData;
  /** Non-fatal issues encountered during import */
  warnings: string[];
  /** Fatal issues that prevented import */
  errors: string[];
}

/** Current export format version */
export const EXPORT_VERSION = '1.0';

// =============================================================================
// FEATURE FLAG TYPE
// =============================================================================

/**
 * Feature flag for controlling simulator visibility
 */
export interface FeatureFlags {
  /** Whether the simulator is visible to users */
  energyCertificateSimulatorEnabled: boolean;
}
